import { Location } from "vue-router";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import store from "@/store";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { EVENTS, ROLES } from "@/const";

export enum MENU_MODULES {
  NSFW_USER_SETTINGS = "NSFW_USER_SETTINGS",
  SEPARATOR = "SEPARATOR",
  PROFILE_INFO = "PROFILE_INFO",
  PWA_BANNER = "PWA_BANNER",
}

interface FilterOptions {
  requiredRole?: string[];
  forbiddenRole?: string;
  requiredAuthorities?: string[];
  requiredCreditCard?: boolean;
}

type IMenuItem = { title: string; hideBack: boolean; list: IMoreMenuItem[] };

export interface IMoreMenuItem {
  label: string;
  name: string;
  icon?: string;
  class?: string;
  to?: Location;
  href?: string;
  filter?: FilterOptions;
  group?: MenuGroups;
  on?: () => void;
}

export enum MenuGroups {
  PWA = "PWA",
  USER = "USER",
  ROLE_SPECIFIC = "ROLE_SPECIFIC",
  ACCOUNT = "ACCOUNT",
  PRICE_SETTINGS = "PRICE_SETTINGS",
  STATISTICS = "STATISTICS",
  CONTENT = "CONTENT",
  NSFW = "NSFW",
  SUPPORT = "SUPPORT",
  DEV_ONLY = "DEV_ONLY",
  LOGOUT = "LOGOUT",
  OTHER = "OTHER",
}

const defaultMenu: () => IMenuItem = () => {
  const authorities = store.state.authorities;
  const user = store.state.user;

  return {
    title: $t("Menu"),
    hideBack: true,
    list: [
      {
        label: "",
        name: MENU_MODULES.PWA_BANNER,
        class: "navigation-menu__pwa-banner",
        group: MenuGroups.PWA,
      },
      {
        label: "",
        name: MENU_MODULES.PROFILE_INFO,
        group: MenuGroups.USER,
      },
      {
        label: $t("Admin Dashboard"),
        name: "adminDashboard",
        group: MenuGroups.ROLE_SPECIFIC,
        icon: "sci-dashbord",
        class: "__admin-dashboard",
        // the panel takes the session of the site user, not an older one of its own (onlyfats CO-15589)
        href: "/admin/?from=site",
        filter: {
          requiredAuthorities: ["canLoginToAdmin"],
        },
      },
      ...(!config.featureSoloModel && !config.featureHideBecomeCreator
        ? [
            {
              label: $t("Become a creator"),
              name: "becomeCreator",
              group: MenuGroups.ROLE_SPECIFIC,
              icon: "sci-rocket",
              class: "__become-creator",
              to: {
                name: "profilePayeeInfo",
              },
              filter: {
                requiredRole: [ROLES.MEMBER],
              },
            },
          ]
        : []),
      {
        label: $t("My profile"),
        name: "profile",
        group: MenuGroups.ACCOUNT,
        icon: "sci-account-circle-outline",
        to: { name: "myProfile" },
      },
      {
        label: $t("Account settings"),
        name: "accountSettings",
        group: MenuGroups.ACCOUNT,
        icon: "msr-manage_accounts",
        to: {
          name: "more",
          query: {
            section: "account",
          },
        },
      },
      {
        label: $t("Notification settings"),
        name: "notifications",
        group: MenuGroups.ACCOUNT,
        icon: "msr-notification_settings",
        to: {
          name: "notificationSettings",
        },
      },
      {
        label: $t("Balance history"),
        name: "balanceHistory",
        group: MenuGroups.ACCOUNT,
        icon: "sci-history",
        to: {
          name: "balance",
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
        },
      },
      {
        label: $t("AI chat bot"),
        name: "aiQuestionary",
        group: MenuGroups.CONTENT,
        icon: "sci-three-stars-outline",
        to: {
          name: "aiQuestionary",
          params: { userId: user?.id ?? "" },
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
          requiredAuthorities: ["getMyAiUserSettings", "updateMyAiUserQuestionary"],
        },
      },
      {
        label: $t("Vault"),
        name: "manageVault",
        group: MenuGroups.CONTENT,
        icon: "sci-vault",
        to: {
          name: "manageVault",
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
          requiredAuthorities: ["updateMyVault", "getMyVault"],
        },
      },
      {
        label: $t("Purchase gallery"),
        name: "purchaseGallery",
        group: MenuGroups.CONTENT,
        icon: "msr-gallery_thumbnail",
        to: {
          name: "purchaseGalleryProviders",
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
          requiredAuthorities: ["getMyPurchasedItems"],
        },
      },
      ...(config.featureLikedContent
        ? [
            {
              label: $t("Liked content"),
              name: "purchaseGallery",
              group: MenuGroups.CONTENT,
              icon: "sci-heart-outline",
              to: {
                name: "likedContent",
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
              },
            },
          ]
        : []),
      ...(config.featureFundraisingGoals
        ? [
            {
              label: $t("My rewards"),
              name: "rewards",
              group: MenuGroups.CONTENT,
              icon: "msr-trophy",
              to: {
                name: "myRewards",
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
              },
            },
          ]
        : []),
      ...(config.featureStatements
        ? [
            {
              label: $t("Statements"),
              name: "statements",
              group: MenuGroups.STATISTICS,
              icon: "sci-chart",
              to: {
                name: "statements",
              },
              filter: {
                requiredRole: [ROLES.CREATOR, ROLES.TALENT_AGENT],
              },
            },
          ]
        : []),
      ...(checkAuthorities(authorities ?? [], ["updateMyContentPrice", "getMyMassMessageStat"])
        ? [
            {
              label: $t("Mass messages statistics"),
              name: "massMessagesStatistics",
              group: MenuGroups.STATISTICS,
              icon: "msr-insert_chart",
              to: {
                name: "massMessagesStatistics",
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
                requiredAuthorities: ["getMyMassMessageStat"],
              },
            },
          ]
        : []),
      ...(config.featureFundraisingGoals && checkAuthorities(authorities ?? [], ["updateMyFundraisingGoal"])
        ? [
            {
              label: $t("Goals"),
              name: "goals",
              group: MenuGroups.STATISTICS,
              icon: "sci-flag-outline",
              to: {
                name: "goals",
              },
              filter: {
                requiredRole: [ROLES.CREATOR],
              },
            },
          ]
        : []),
      ...(config.featurePayoutRequest
        ? [
            {
              label: $t("Payout requests"),
              name: "payoutRequests",
              group: MenuGroups.ACCOUNT,
              icon: "sci-cashback",
              to: {
                name: "payoutRequests",
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
                requiredAuthorities: ["getMyPayoutRequest"],
              },
            },
          ]
        : []),
      {
        label: $t("Lists"),
        name: "groups",
        group: MenuGroups.CONTENT,
        icon: "sci-list",
        to: {
          name: "manageGroups",
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
          requiredAuthorities: ["manageMyGroups"],
        },
      },
      ...(config.featureStories
        ? [
            {
              label: $t("Stories archive"),
              name: "storiesArchive",
              group: MenuGroups.CONTENT,
              icon: "sci-stories-archive",
              to: {
                name: "storiesArchive",
              },
              filter: {
                requiredRole: [ROLES.CREATOR],
              },
            },
          ]
        : []),
      ...(config.featureEvents
        ? [
            {
              label: $t("Events"),
              icon: "msr-event",
              name: "events",
              group: MenuGroups.CONTENT,
              to: {
                name: "events",
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
              },
            },
          ]
        : []),
      ...(config.featureAppointments
        ? [
            {
              label: $t("Appointments"),
              name: "dates",
              group: MenuGroups.CONTENT,
              icon: "sci-calendar-heart",
              to: {
                name: "appointments",
                query: {
                  tab: checkRole(user?.role || "", ROLES.CREATOR) ? "requests" : "my-upcoming",
                },
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
              },
            },
          ]
        : []),

      {
        label: $t("Price settings"),
        name: "priceSettings",
        group: MenuGroups.PRICE_SETTINGS,
        icon: "sci-money-edit",
        to: {
          name: "priceMenu",
        },
      },
      {
        label: $t("My subscriptions"),
        name: "subscriptionManagement",
        group: checkRole(user?.role || "", ROLES.CREATOR) ? MenuGroups.PRICE_SETTINGS : MenuGroups.ACCOUNT,
        icon: "msr-groups",
        to: {
          name: "manageRecurring",
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
        },
      },
      ...(config.featureWelcomeMessage
        ? [
            {
              label: $t("Welcome message"),
              name: "subAndFollow",
              group: MenuGroups.PRICE_SETTINGS,
              icon: "sci-waving-hand",
              to: {
                name: "followingSettings",
              },
              filter: {
                forbiddenRole: ROLES.ADMIN,
                requiredAuthorities: ["updateMyContentSubscriptionLevel"],
              },
            },
          ]
        : []),
      {
        label: $t("Payment card"),
        name: "paymentCard",
        group: MenuGroups.ACCOUNT,
        icon: "sci-credit-card-outline",
        to: {
          name: "profilePaymentCards",
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
          requiredCreditCard: true,
        },
      },
      {
        label: $t("Payee information"),
        name: "payeeInformation",
        group: MenuGroups.ACCOUNT,
        icon: "msr-info",
        to: {
          name: "profilePayeeInfo",
        },
        filter: {
          requiredRole: [ROLES.CREATOR],
        },
      },
      {
        label: $t("Invite friends"),
        name: "inviteFriends",
        group: MenuGroups.SUPPORT,
        icon: "sci-account-circle-outline",
        to: {
          name: "referrals",
        },
        filter: {
          requiredRole: [ROLES.CREATOR, ROLES.TALENT_AGENT],
        },
      },
      {
        label: $t("Creators by talent rep"),
        name: "creatorsByTalentRep",
        group: MenuGroups.ROLE_SPECIFIC,
        icon: "msr-group",
        to: {
          name: "creatorsByTalentRep",
        },
        filter: {
          requiredRole: [ROLES.TALENT_AGENT],
        },
      },
      ...(config.featureNSFW
        ? [
            {
              label: "",
              name: MENU_MODULES.NSFW_USER_SETTINGS,
              group: MenuGroups.NSFW,
            },
          ]
        : []),
      {
        label: $t("Support"),
        name: "support",
        group: MenuGroups.SUPPORT,
        icon: "msr-live_help",
        to: {
          name: "messages",
          params: {
            userId: "1",
          },
        },
        filter: {
          forbiddenRole: ROLES.ADMIN,
        },
      },
      {
        label: $t("Terms & Policies"),
        name: "terms",
        group: MenuGroups.SUPPORT,
        icon: "msr-docs",
        to: {
          name: "more",
          query: {
            section: "terms",
          },
        },
      },
      ...(config.featureFlagsDisplay
        ? [
            {
              label: $t("Features flags"),
              name: "featuresFlags",
              group: MenuGroups.DEV_ONLY,
              to: {
                name: "featuresFlags",
              },
            },
          ]
        : []),
      {
        label: $t("Logout"),
        name: "logout",
        group: MenuGroups.LOGOUT,
        icon: "sci-logout",
        class: "__negative",
        on: () => {
          emitter.emit(EVENTS.USER_LOGOUT);
        },
      },
    ],
  };
};

const termsMenu: IMenuItem = {
  hideBack: false,
  title: $t("Terms & Policies"),
  list: [],
};

const accountMenu: () => IMenuItem = () => {
  const user = store.state.user;
  return {
    hideBack: false,
    title: $t("Account settings"),
    list: [
      ...(user?.email
        ? [
            {
              label: user?.email,
              name: "userEmail",
              class: "__disabled",
            },
          ]
        : []),
      ...(user?.phone
        ? [
            {
              label: user?.phone || "",
              name: "userPhone",
              class: "__disabled",
            },
          ]
        : []),
      ...(user?.email
        ? [
            {
              label: $t("Change password"),
              name: "changePassword",
              to: {
                name: "changePassword",
              },
            },
          ]
        : []),
      {
        label: $t("Delete account"),
        name: "deleteAccount",
        to: {
          name: "deleteAccount",
        },
        filter: {
          requiredAuthorities: ["disableMyUser"],
        },
      },
    ],
  };
};

const moreMenu: Record<string, IMenuItem> = {
  get default() {
    return defaultMenu();
  },
  terms: termsMenu,
  get account() {
    return accountMenu();
  },
};

export { moreMenu };
